import {
  ArrowDownward,
  ArrowUpward,
  CalendarToday,
  Description,
  ExpandLess,
  ExpandMore,
  Folder,
  FolderOpen,
  Search,
  SortByAlpha,
  ViewList,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  useDatasetList,
  useGetDatasetFolders,
} from "src/views/datasets/services/DatasetServices";
import { useEffect, useMemo, useState } from "react";

import DatasetTypeChip from "src/views/datasets/components/DatasetTypeChip";

interface DatasetSelectorContentProps {
  /** Source of truth for the current selection. */
  selectedDatasets: any[];
  /** Emits the new selection array (objects, not ids). */
  onSelectionChange: (datasets: any[]) => void;
  /** Show a removable-chip summary of the selection above the tree. */
  showChips?: boolean;
}

const areSetsEqual = (a: Set<number>, b: Set<number>) => {
  if (a.size !== b.size) {
    return false;
  }

  for (const value of a) {
    if (!b.has(value)) {
      return false;
    }
  }

  return true;
};

/**
 * DatasetSelectorContent - The folder-aware dataset picker body.
 *
 * Selection is fully controlled: `selectedDatasets` is the single source of
 * truth for the emitted array (never derived from the fetched lookup), so a
 * selected-but-not-yet-fetched item (e.g. an AI suggestion) stays selected and
 * is never silently dropped.
 *
 * Used both inline (agent forms) and inside DatasetSelectorModal (chat etc.).
 */
const DatasetSelectorContent = ({
  selectedDatasets,
  onSelectionChange,
  showChips = false,
}: DatasetSelectorContentProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "date">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [viewMode, setViewMode] = useState<"all" | "folders" | "datasets">(
    "all",
  );
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(
    new Set(),
  );

  const normalizedSearchQuery = debouncedSearchQuery.trim().toLowerCase();

  // Fetch all datasets (including folders). SSE keeps these caches fresh via
  // notify:datasets:changed, so we don't need to refetch on open.
  const { isLoading: isDatasetListLoading, data: datasetList } = useDatasetList(
    false,
    null,
    true,
  );

  const { isLoading: isFoldersLoading, data: datasetFolders } =
    useGetDatasetFolders();

  // Combine datasets and folders into a single list
  const foldersList = datasetFolders || [];
  const datasetsList = datasetList || [];
  const combinedDataList = [...foldersList, ...datasetsList];

  // Lookup map for resolving an id to its dataset object. Built from the
  // fetched data and unioned with the controlled selection so chip labels and
  // tree rows resolve for pre-selected items that aren't in the fetched list.
  // NOTE: this is for display only — the emitted array is always sourced from
  // the `selectedDatasets` prop, never from this lookup.
  const datasetLookup = new Map<number, any>();
  combinedDataList.forEach((item: any) => {
    if (item?.datasets?.length) {
      item.datasets.forEach((ds: any) => {
        if (typeof ds?.id === "number") {
          datasetLookup.set(ds.id, ds);
        }
      });
    } else if (typeof item?.id === "number") {
      datasetLookup.set(item.id, item);
    }
  });
  (selectedDatasets ?? []).forEach((ds: any) => {
    if (typeof ds?.id === "number" && !datasetLookup.has(ds.id)) {
      datasetLookup.set(ds.id, ds);
    }
  });

  // Selected ids derived from the prop — drives checkbox checked-state.
  const selectedDatasetIds = useMemo(
    () =>
      new Set<number>(
        (selectedDatasets ?? [])
          .map((ds: any) => ds?.id)
          .filter((id: any): id is number => typeof id === "number"),
      ),
    [selectedDatasets],
  );

  const isLoading = isDatasetListLoading || isFoldersLoading;

  // Debounce search query for better performance with large datasets
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 150);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter datasets based on debounced search query
  let filteredDatasets = [];
  if (combinedDataList) {
    if (!normalizedSearchQuery) {
      filteredDatasets = combinedDataList;
    } else {
      filteredDatasets = combinedDataList.filter((item: any) => {
        const nameMatches = item.name
          ?.toLowerCase()
          .includes(normalizedSearchQuery);

        if (item.datasets?.length) {
          const childMatches = item.datasets.some((ds: any) =>
            ds.name?.toLowerCase().includes(normalizedSearchQuery),
          );
          return Boolean(nameMatches || childMatches);
        }

        return Boolean(nameMatches);
      });
    }
  }

  // Sort filtered datasets
  let sortedDatasets = [...filteredDatasets].sort((a: any, b: any) => {
    // Always put folders first
    const aIsFolder = Boolean(a.datasets);
    const bIsFolder = Boolean(b.datasets);

    if (aIsFolder && !bIsFolder) return -1;
    if (!aIsFolder && bIsFolder) return 1;

    // Within same type (folder or dataset), apply user's sort
    let compareValue = 0;
    if (sortBy === "name") {
      compareValue = (a.name || "").localeCompare(b.name || "");
    } else if (sortBy === "date") {
      const aDate = new Date(a.create_date || 0).getTime();
      const bDate = new Date(b.create_date || 0).getTime();
      compareValue = aDate - bDate;
    }

    return sortOrder === "asc" ? compareValue : -compareValue;
  });

  // Apply view mode filter
  if (viewMode === "folders") {
    sortedDatasets = sortedDatasets.filter((item: any) =>
      Boolean(item.datasets),
    );
  } else if (viewMode === "datasets") {
    sortedDatasets = sortedDatasets.filter(
      (item: any) => !item.datasets && !item.dataset_folder_fk,
    );
  }

  // Auto-expand folders containing search results
  useEffect(() => {
    if (!normalizedSearchQuery) return;

    const foldersToExpand = new Set<number>();
    sortedDatasets?.forEach((item: any) => {
      // If it's a dataset with a folder, expand that folder
      if (item.dataset_folder_fk) {
        foldersToExpand.add(item.dataset_folder_fk);
      }
      // If it's a folder with matching datasets, expand it
      if (item.datasets) {
        const hasMatchingDataset = item.datasets.some((ds: any) =>
          ds.name?.toLowerCase().includes(normalizedSearchQuery),
        );
        const folderNameMatches = item.name
          ?.toLowerCase()
          .includes(normalizedSearchQuery);
        if (hasMatchingDataset || folderNameMatches) {
          foldersToExpand.add(item.id);
        }
      }
    });

    setExpandedFolders((prev) =>
      areSetsEqual(prev, foldersToExpand) ? prev : foldersToExpand,
    );
  }, [normalizedSearchQuery, sortedDatasets]);

  // Organize datasets into folder structure
  const folders: any[] = [];
  const orphanDatasets: any[] = [];
  const lowerQuery = normalizedSearchQuery;

  // Helper function to sort datasets
  const sortDatasets = (datasets: any[]) => {
    return [...datasets].sort((a: any, b: any) => {
      let compareValue = 0;
      if (sortBy === "name") {
        compareValue = (a.name || "").localeCompare(b.name || "");
      } else {
        const aDate = new Date(a.create_date || 0).getTime();
        const bDate = new Date(b.create_date || 0).getTime();
        compareValue = aDate - bDate;
      }
      return sortOrder === "asc" ? compareValue : -compareValue;
    });
  };

  sortedDatasets?.forEach((item: any) => {
    if (item.datasets) {
      // This is a folder
      // If searching, filter the datasets within the folder
      if (normalizedSearchQuery) {
        const folderNameMatches = item.name?.toLowerCase().includes(lowerQuery);
        const matchingDatasets = item.datasets.filter((ds: any) =>
          ds.name?.toLowerCase().includes(lowerQuery),
        );

        if (matchingDatasets.length > 0) {
          folders.push({ ...item, datasets: sortDatasets(matchingDatasets) });
        } else if (folderNameMatches) {
          folders.push({ ...item, datasets: sortDatasets(item.datasets) });
        }
      } else {
        folders.push({ ...item, datasets: sortDatasets(item.datasets) });
      }
    } else if (!item.dataset_folder_fk) {
      // This is a dataset without a folder
      orphanDatasets.push(item);
    }
  });

  const foldersWithDatasets = folders;
  const datasetsWithoutFolder = orphanDatasets;

  // Toggle folder expand/collapse
  const toggleFolder = (folderId: number) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  // Toggle dataset selection — emits straight from the controlled prop array.
  const toggleDatasetSelection = (datasetId: number) => {
    const current = selectedDatasets ?? [];
    if (selectedDatasetIds.has(datasetId)) {
      onSelectionChange(current.filter((d: any) => d.id !== datasetId));
    } else {
      const dataset = datasetLookup.get(datasetId);
      if (dataset) {
        onSelectionChange([...current, dataset]);
      }
    }
  };

  const handleClearSelection = () => {
    onSelectionChange([]);
  };

  const handleSelectAll = () => {
    if (!combinedDataList) return;
    // Only select actual datasets, not folders
    const allDatasetObjects = combinedDataList.filter(
      (ds: any) => !ds.datasets,
    );
    onSelectionChange(allDatasetObjects);
  };

  const selectionCount = (selectedDatasets ?? []).length;

  return (
    // flex:1 1 auto (not height:100%) so this fills a definite-height parent
    // (the modal's DialogContent) yet shrinks-to-scroll under a maxHeight-only
    // parent (the inline form box) — a % height would collapse there.
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
      }}
    >
      {/* Search and Action Bar */}
      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={isMobile ? 1 : 2}
        alignItems="stretch"
        sx={{ p: 2, pb: 1 }}
      >
        <TextField
          placeholder="Search datasets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          data-testid="aa-ds-selector-search"
          data-attr="assistant:input:search-datasets"
          inputProps={{
            "data-testid": "aa-ds-selector-search-inner",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            onClick={handleClearSelection}
            disabled={selectionCount === 0}
            fullWidth={isMobile}
            data-attr="assistant:btn:clear-dataset-selection"
          >
            Clear
          </Button>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={sortedDatasets.length === 0}
            fullWidth={isMobile}
            data-attr="assistant:btn:select-all-datasets"
          >
            Select All
          </Button>
        </Stack>
      </Stack>

      {/* Sort and View Controls */}
      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={isMobile ? 1 : 2}
        alignItems={isMobile ? "stretch" : "center"}
        sx={{ px: 2, pb: 1 }}
      >
        {!isMobile && (
          <Typography variant="body2" color="text.secondary">
            View:
          </Typography>
        )}
        <Stack direction="row" spacing={1} alignItems="center" flex={1}>
          {isMobile && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 40 }}
            >
              View:
            </Typography>
          )}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newValue) => {
              if (newValue !== null) setViewMode(newValue);
            }}
            size="small"
            fullWidth={isMobile}
          >
            <ToggleButton
              value="all"
              data-attr="assistant:toggle:view-all-datasets"
            >
              <Tooltip title="Show All">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ViewList fontSize="small" />
                  {!isMobile && <Typography variant="caption">All</Typography>}
                </Stack>
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value="folders"
              data-attr="assistant:toggle:view-folders-only"
            >
              <Tooltip title="Folders Only">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Folder fontSize="small" />
                  {!isMobile && (
                    <Typography variant="caption">Folders</Typography>
                  )}
                </Stack>
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value="datasets"
              data-attr="assistant:toggle:view-datasets-only"
            >
              <Tooltip title="Datasets Only">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Description fontSize="small" />
                  {!isMobile && (
                    <Typography variant="caption">Datasets</Typography>
                  )}
                </Stack>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {!isMobile && <Divider orientation="vertical" flexItem />}
        <Stack direction="row" spacing={1} alignItems="center" flex={1}>
          {isMobile && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 40 }}
            >
              Sort:
            </Typography>
          )}
          {!isMobile && (
            <Typography variant="body2" color="text.secondary">
              Sort by:
            </Typography>
          )}
          <ToggleButtonGroup
            value={sortBy}
            exclusive
            onChange={(_, newValue) => {
              if (newValue !== null) setSortBy(newValue);
            }}
            size="small"
            fullWidth={isMobile}
          >
            <ToggleButton
              value="name"
              data-attr="assistant:toggle:sort-datasets-by-name"
            >
              <Tooltip title="Sort by Name">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <SortByAlpha fontSize="small" />
                  {!isMobile && <Typography variant="caption">Name</Typography>}
                </Stack>
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value="date"
              data-attr="assistant:toggle:sort-datasets-by-date"
            >
              <Tooltip title="Sort by Date">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <CalendarToday fontSize="small" />
                  {!isMobile && <Typography variant="caption">Date</Typography>}
                </Stack>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton
            size="small"
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            data-attr="assistant:btn:toggle-dataset-sort-order"
          >
            <Tooltip title={sortOrder === "asc" ? "Ascending" : "Descending"}>
              {sortOrder === "asc" ? (
                <ArrowUpward fontSize="small" />
              ) : (
                <ArrowDownward fontSize="small" />
              )}
            </Tooltip>
          </IconButton>
        </Stack>
      </Stack>

      {/* Selected Datasets Summary */}
      <Box sx={{ px: 2, pb: 1, flexShrink: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {selectionCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              Selected: {selectionCount} dataset
              {selectionCount !== 1 ? "s" : ""}
            </Typography>
          )}
          {debouncedSearchQuery && (
            <Typography variant="body2" color="text.secondary">
              Showing: {sortedDatasets.length} result
              {sortedDatasets.length !== 1 ? "s" : ""}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Selected Datasets Chips */}
      {showChips && selectionCount > 0 && (
        <Box
          sx={{
            px: 2,
            pb: 1,
          }}
        >
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {(selectedDatasets ?? []).map((dataset: any) => (
              <Chip
                key={`selected-${dataset.id}`}
                label={
                  dataset.name ||
                  datasetLookup.get(dataset.id)?.name ||
                  `Dataset ${dataset.id}`
                }
                size="small"
                onDelete={() => toggleDatasetSelection(dataset.id)}
                data-attr="assistant:chip:selected-dataset"
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Dataset Tree View */}
      <Box
        sx={{
          flex: 1,
          minHeight: 100,
          maxHeight: 500,
          px: 2,
          overflowY: "auto",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>Loading datasets...</Typography>
          </Box>
        ) : (
          <List
            sx={{ width: "100%", bgcolor: "background.paper" }}
            data-testid="aa-ds-selector-list"
          >
            {/* Datasets without folder */}
            {datasetsWithoutFolder.map((dataset: any) => (
              <ListItem
                key={`dataset-${dataset.id}`}
                disablePadding
                data-testid={`aa-ds-selector-item-${dataset.id}`}
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <ListItemButton
                  onClick={() => toggleDatasetSelection(dataset.id)}
                  dense
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedDatasetIds.has(dataset.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemIcon>
                    <DatasetTypeChip
                      avatar
                      type={dataset.dataset_type?.name}
                      fontSize={18}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={dataset.name}
                    secondary={dataset.description}
                    primaryTypographyProps={{
                      fontWeight: "bold",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}

            {/* Folders with datasets */}
            {foldersWithDatasets.map((folder: any) => (
              <Box
                key={`folder-${folder.id}`}
                data-testid={`aa-ds-selector-folder-${folder.id}`}
              >
                <ListItem
                  disablePadding
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <ListItemButton onClick={() => toggleFolder(folder.id)}>
                    <ListItemIcon>
                      {expandedFolders.has(folder.id) ? (
                        <FolderOpen color="primary" />
                      ) : (
                        <Folder color="primary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={folder.name}
                      secondary={`${folder.datasets?.length || 0} dataset${
                        folder.datasets?.length !== 1 ? "s" : ""
                      }`}
                      primaryTypographyProps={{
                        fontWeight: "bold",
                      }}
                    />
                    {expandedFolders.has(folder.id) ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Datasets in folder */}
                {expandedFolders.has(folder.id) &&
                  folder.datasets?.map((dataset: any) => (
                    <ListItem
                      key={`dataset-${dataset.id}`}
                      disablePadding
                      data-testid={`aa-ds-selector-item-${dataset.id}`}
                      sx={{
                        pl: 4,
                        borderBottom: 1,
                        borderColor: "divider",
                        bgcolor: theme.palette.action.hover,
                      }}
                    >
                      <ListItemButton
                        onClick={() => toggleDatasetSelection(dataset.id)}
                        dense
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedDatasetIds.has(dataset.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemIcon>
                          <DatasetTypeChip
                            avatar
                            type={dataset.dataset_type?.name}
                            fontSize={18}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={dataset.name}
                          secondary={dataset.description}
                          primaryTypographyProps={{
                            fontWeight: "bold",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
              </Box>
            ))}

            {/* No results message */}
            {datasetsWithoutFolder.length === 0 &&
              foldersWithDatasets.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 200,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No datasets found
                  </Typography>
                </Box>
              )}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default DatasetSelectorContent;
